import DealFilters from '@/components/all-from-router/_components/deal-filters/deal-filters';
import {
  FilteredDealsProvider,
  useFilteredDealsContext,
} from '@/components/all-from-router/_components/deal-filters/filtered-deals/filtered-deals-provider';
import VirtualizedDealBoard from '@/components/board-from-router/_components/virtualized-deal-board.tsx';
import DisplayButton from '@/components/ui/deal-list/diplay/display-button.tsx';
import type { ViewOption } from '@/components/ui/deal-list/diplay/display-selector.tsx';
import VirtualizedDealList from '@/components/ui/deal-list/virtualized-deals-list';
import FindInViewFilter from '@/components/ui/find-in-view-filter/find-in-view-filter';
import { useFindInViewFilterContext } from '@/components/ui/find-in-view-filter/find-in-view-filter-provider';
import { HubListHeader } from '@/components/ui/layout/headers';
import HotkeyDialogBoxes from '@/components/ux/all-list-and-board/hotkey-dialog-boxes';
import useAllListAndBoardHotkeys from '@/components/ux/all-list-and-board/use-all-list-and-board-hotkeys';
import useDealList from '@/hooks/use-deal-list';
import useLocalStorage from '@/hooks/use-local-storage.ts';
import { type DealListItem } from '@/models/types';
import { createFileRoute } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import useMyDealsFilter from '../../../../hooks/use-my-deals-filter';

export const Route = createFileRoute('/(authenticated)/$hub/my-deals/')({
  component: MyDealsPageWrapper,
  staticData: {
    keepAlive: true,
  },
});

const MyDealsPage = observer(() => {
  const { dealsListData: dealsData } = useDealList();

  const { openDialog, setOpenDialog, selectedDeal, enableScope } =
    useAllListAndBoardHotkeys();

  const [viewMode, setViewMode] = useLocalStorage<ViewOption>(
    'my-deals-display-mode',
    'list',
  );

  const { filterCallback, shouldApplyFilteredDealsIds } =
    useFilteredDealsContext();
  const { findInViewQuery } = useFindInViewFilterContext();

  const findInViewFilterCallback = useCallback(
    (deal: DealListItem) => {
      const query = findInViewQuery.toLowerCase();
      const vehicleString = `${deal.vehicle?.vehicle?.year} ${deal.vehicle?.vehicle?.make} ${deal.vehicle?.vehicle?.model} ${deal.vehicle?.vehicle?.trim}`;
      const vin = deal.vehicle?.vehicle?.vin;

      return (
        deal.short_id.toLowerCase().includes(query) ||
        deal.customer_name?.toLowerCase().includes(query) ||
        deal.customer_email?.toLowerCase().includes(query) ||
        vehicleString.toLowerCase().includes(query) ||
        vin?.toLowerCase().includes(query) ||
        false
      );
    },
    [findInViewQuery],
  );

  return (
    <div>
      <div className="flex flex-col h-screen">
        <HubListHeader
          findInViewFilter={<FindInViewFilter />}
          subRow={
            <>
              <DealFilters />
              <DisplayButton
                viewMode={viewMode}
                onViewModeChange={setViewMode}
              />
            </>
          }
        />

        {viewMode === 'list' ? (
          <VirtualizedDealList
            dealsData={dealsData}
            filterCallback={filterCallback}
            enableScope={enableScope}
            findInViewFilterCallback={findInViewFilterCallback}
          />
        ) : (
          <VirtualizedDealBoard
            dealsData={dealsData}
            filterCallback={filterCallback}
            enableScope={enableScope}
            shouldApplyFilteredDealsIds={shouldApplyFilteredDealsIds}
            findInViewFilterCallback={findInViewFilterCallback}
          />
        )}
      </div>

      <HotkeyDialogBoxes
        openDialog={openDialog}
        setOpenDialog={setOpenDialog}
        selectedDeal={selectedDeal}
      />
    </div>
  );
});

export const MyDealsFilteredDealsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const myDealsFilter = useMyDealsFilter();

  return (
    <FilteredDealsProvider
      initialFilteredState={myDealsFilter ? [myDealsFilter] : []}
    >
      {children}
    </FilteredDealsProvider>
  );
};

function MyDealsPageWrapper() {
  return (
    <MyDealsFilteredDealsProvider>
      <MyDealsPage />
    </MyDealsFilteredDealsProvider>
  );
}
